// apps/accounts/src/pages/LabourBills.jsx
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import pb from '../lib/pocketbaseClient';
import {
  Users,
  Search,
  DollarSign,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Calendar,
  Filter,
  AlertCircle,
  CreditCard,
  Plus,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';
import { formatDate } from '../utils/dateHelpers';
import { cn } from '../lib/utils';

export default function LabourBills() {
  const { user, role } = useAuth();
  const [loading, setLoading] = useState(true);
  const [labours, setLabours] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [sites, setSites] = useState([]);
  const [attendanceData, setAttendanceData] = useState({});
  const [paymentData, setPaymentData] = useState({});
  const [filterVendor, setFilterVendor] = useState('All');
  const [filterSite, setFilterSite] = useState('All');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedLabour, setSelectedLabour] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentNote, setPaymentNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const [laboursRes, vendorsRes, sitesRes] = await Promise.all([
        pb.collection('labour').getFullList({
          sort: 'name',
          expand: 'vendor,site',
          filter: 'status="active"',
        }),
        pb.collection('vendors').getFullList({ sort: 'name' }),
        pb.collection('accounts_projects').getFullList({ sort: 'name' }),
      ]);
      setLabours(laboursRes);
      setVendors(vendorsRes);
      setSites(sitesRes);

      const today = new Date();
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(today.getMonth() - 3);
      const defaultFrom = threeMonthsAgo.toISOString().split('T')[0];
      const defaultTo = today.toISOString().split('T')[0];
      setDateFrom(defaultFrom);
      setDateTo(defaultTo);
      await fetchAttendanceAndPayments(laboursRes, defaultFrom, defaultTo);
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to load data. Please check console for details.');
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceAndPayments = async (laboursList, from, to) => {
    const attMap = {};
    const payMap = {};
    for (const lab of laboursList) {
      try {
        const attRecords = await pb.collection('labour_attendance').getFullList({
          filter: `labour="${lab.id}" && date >= "${from}" && date <= "${to}"`,
        });
        attMap[lab.id] = attRecords;

        const payRecords = await pb.collection('labour_salary_transactions').getFullList({
          filter: `labour="${lab.id}" && date >= "${from}" && date <= "${to}"`,
        });
        payMap[lab.id] = payRecords;
      } catch (err) {
        console.error(`Error fetching for labour ${lab.id}:`, err);
        attMap[lab.id] = [];
        payMap[lab.id] = [];
      }
    }
    setAttendanceData(attMap);
    setPaymentData(payMap);
  };

  const handleSearch = async () => {
    if (!dateFrom || !dateTo) {
      toast.error('Please select date range');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await fetchAttendanceAndPayments(labours, dateFrom, dateTo);
      toast.success('Data updated');
    } catch (err) {
      setError('Failed to update data');
    } finally {
      setLoading(false);
    }
  };

  const getAttendanceStats = (labId) => {
    const records = attendanceData[labId] || [];
    const present = records.filter(r => r.status === 'present').length;
    const halfDay = records.filter(r => r.status === 'halfday').length;
    const absent = records.filter(r => r.status === 'absent').length;
    return { present, halfDay, absent, total: records.length };
  };

  const getPayments = (labId) => {
    const records = paymentData[labId] || [];
    const totalPaid = records
      .filter(r => r.type === 'payment')
      .reduce((sum, r) => sum + (r.amount || 0), 0);
    const totalAdvance = records
      .filter(r => r.type === 'advance')
      .reduce((sum, r) => sum + (r.amount || 0), 0);
    return { totalPaid, totalAdvance, records };
  };

  const calculateSalary = (lab) => {
    const stats = getAttendanceStats(lab.id);
    const dailyRate = lab.dailyRate || 0;
    const totalDays = stats.present + (stats.halfDay * 0.5);
    return totalDays * dailyRate;
  };

  const filteredLabours = labours.filter(lab => {
    const matchVendor = filterVendor === 'All' || lab.vendor === filterVendor;
    const matchSite = filterSite === 'All' || lab.site === filterSite;
    const matchSearch = lab.name?.toLowerCase().includes(search.toLowerCase()) ||
                        lab.aadhar?.includes(search);
    return matchVendor && matchSite && matchSearch;
  });

  const grandTotals = filteredLabours.reduce((acc, lab) => {
    const salary = calculateSalary(lab);
    const payments = getPayments(lab.id);
    const due = salary - payments.totalPaid;
    acc.totalSalary += salary;
    acc.totalPaid += payments.totalPaid;
    acc.totalDue += due;
    acc.totalAdvance += payments.totalAdvance;
    return acc;
  }, { totalSalary: 0, totalPaid: 0, totalDue: 0, totalAdvance: 0 });

  // --- Payment Modal Handlers ------------------------------------------
  const openPaymentModal = (lab) => {
    setSelectedLabour(lab);
    setPaymentAmount('');
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setPaymentNote('');
    setShowPaymentModal(true);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!selectedLabour) return;
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setSubmitting(true);
    try {
      await pb.collection('labour_salary_transactions').create({
        labour: selectedLabour.id,
        amount: parseFloat(paymentAmount),
        type: 'payment',
        note: paymentNote || 'Salary payment',
        date: paymentDate,
      });
      toast.success(`Payment of Rs.${parseFloat(paymentAmount).toFixed(2)} recorded for ${selectedLabour.name}`);
      setShowPaymentModal(false);
      setSelectedLabour(null);
      // Refresh data
      await fetchAttendanceAndPayments(labours, dateFrom, dateTo);
      // Update grand totals after refresh
      // Since we call fetchAttendanceAndPayments which updates state, it will re-render
    } catch (err) {
      console.error(err);
      toast.error('Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Format currency ------------------------------------------------
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null || isNaN(amount)) return 'Rs.0.00';
    return `Rs.${parseFloat(amount).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500 font-medium">Loading labour bills...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-amber-500" />
            <span>Labour Bills</span>
            <Badge className="bg-amber-100 text-amber-700 ml-2">{filteredLabours.length}</Badge>
          </h1>
          <p className="text-sm text-slate-500">View labour bills, payments, and dues by site/vendor/date</p>
        </div>
        <Button
          variant="outline"
          onClick={handleSearch}
          className="border-blue-500 text-blue-600 hover:bg-blue-50"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 p-3 rounded-lg border border-red-200 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-slate-100">
        <div className="flex-1 min-w-[200px]">
          <Input
            placeholder="Search labour..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <select
          value={filterVendor}
          onChange={(e) => setFilterVendor(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-400"
        >
          <option value="All">All Vendors</option>
          {vendors.map(v => (
            <option key={v.id} value={v.id}>{v.name}</option>
          ))}
        </select>
        <select
          value={filterSite}
          onChange={(e) => setFilterSite(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-400"
        >
          <option value="All">All Sites</option>
          {sites.map(s => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-36"
          />
          <span className="text-slate-400">to</span>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-36"
          />
        </div>
        <Button
          onClick={handleSearch}
          className="bg-amber-500 hover:bg-amber-600 text-white"
        >
          <Search className="w-4 h-4 mr-2" />
          Apply
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            setSearch('');
            setFilterVendor('All');
            setFilterSite('All');
            const today = new Date().toISOString().split('T')[0];
            const threeMonthsAgo = new Date();
            threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
            setDateFrom(threeMonthsAgo.toISOString().split('T')[0]);
            setDateTo(today);
            handleSearch();
          }}
        >
          Reset
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">Total Labours</p>
                <p className="text-2xl font-bold text-slate-800">{filteredLabours.length}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">Total Salary</p>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(grandTotals.totalSalary)}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">Total Paid</p>
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(grandTotals.totalPaid)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className={grandTotals.totalDue > 0 ? "border-l-4 border-l-red-500" : "border-l-4 border-l-green-500"}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">Total Due</p>
                <p className={grandTotals.totalDue > 0 ? "text-2xl font-bold text-red-600" : "text-2xl font-bold text-green-600"}>
                  {grandTotals.totalDue > 0 ? '+' : ''}{formatCurrency(grandTotals.totalDue)}
                </p>
              </div>
              <TrendingDown className={grandTotals.totalDue > 0 ? "w-8 h-8 text-red-500" : "w-8 h-8 text-green-500"} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table with Payment Button */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Name</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Vendor</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Site</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Daily Rate</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-center">Present</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-center">Half Day</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-center">Absent</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-right">Salary</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-right">Paid</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-right">Due</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredLabours.length === 0 ? (
                <tr>
                  <td colSpan="11" className="px-4 py-8 text-center text-slate-500">
                    No labours found in the selected period.
                  </td>
                </tr>
              ) : (
                filteredLabours.map((lab) => {
                  const vendor = vendors.find(v => v.id === lab.vendor);
                  const site = sites.find(s => s.id === lab.site);
                  const stats = getAttendanceStats(lab.id);
                  const salary = calculateSalary(lab);
                  const payments = getPayments(lab.id);
                  const due = salary - payments.totalPaid;

                  return (
                    <tr key={lab.id} className="hover:bg-slate-50/50 transition">
                      <td className="px-4 py-3 font-medium flex items-center gap-2">
                        {lab.photo && (
                          <img
                            src={pb.files.getUrl(lab, lab.photo)}
                            alt={lab.name}
                            className="w-6 h-6 rounded-full object-cover border"
                          />
                        )}
                        {lab.name}
                      </td>
                      <td className="px-4 py-3">{vendor?.name || 'N/A'}</td>
                      <td className="px-4 py-3">{site?.name || 'N/A'}</td>
                      <td className="px-4 py-3">{formatCurrency(lab.dailyRate || 0)}</td>
                      <td className="px-4 py-3 text-center font-medium text-emerald-600">{stats.present}</td>
                      <td className="px-4 py-3 text-center font-medium text-yellow-600">{stats.halfDay}</td>
                      <td className="px-4 py-3 text-center font-medium text-red-600">{stats.absent}</td>
                      <td className="px-4 py-3 text-right font-bold text-emerald-600">{formatCurrency(salary)}</td>
                      <td className="px-4 py-3 text-right font-bold text-amber-600">{formatCurrency(payments.totalPaid)}</td>
                      <td className={cn("px-4 py-3 text-right font-bold", due > 0 ? "text-red-600" : "text-green-600")}>
                        {due > 0 ? '+' : ''}{formatCurrency(due)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                          onClick={() => openPaymentModal(lab)}
                          title="Record Payment"
                        >
                          <CreditCard className="w-4 h-4 mr-1" />
                          Pay
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            <tfoot className="bg-slate-50 font-semibold">
              <tr>
                <td colSpan="7" className="px-4 py-3 text-right">Grand Total:</td>
                <td className="px-4 py-3 text-right text-emerald-600">{formatCurrency(grandTotals.totalSalary)}</td>
                <td className="px-4 py-3 text-right text-amber-600">{formatCurrency(grandTotals.totalPaid)}</td>
                <td className={cn("px-4 py-3 text-right", grandTotals.totalDue > 0 ? "text-red-600" : "text-green-600")}>
                  {grandTotals.totalDue > 0 ? '+' : ''}{formatCurrency(grandTotals.totalDue)}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </CardContent>
      </Card>

      {/* Payment Modal */}
      {showPaymentModal && selectedLabour && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-amber-500" />
                Record Payment
              </h3>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Labour: <span className="font-medium text-slate-700">{selectedLabour.name}</span>
            </p>
            <form onSubmit={handlePaymentSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Amount (Rs.)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder="0.00"
                  required
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
                <Input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Note (optional)</label>
                <Input
                  value={paymentNote}
                  onChange={(e) => setPaymentNote(e.target.value)}
                  placeholder="Payment reference..."
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowPaymentModal(false)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                  disabled={submitting}
                >
                  {submitting ? 'Processing...' : 'Record Payment'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}